'use client'
import { Button } from '@/components/Button'
import { FormInput } from '@/components/FormInput'
import { Icon } from '@/icons'
import { AppSearchMktuFilterPanel } from '@/modules/OnlineSearch/components/AppSearchMktuFilterPanel'
import { onlineSearchStore } from '@/store/OnlineSearch.store'
import cn from 'classnames'
import { observer } from 'mobx-react-lite'
import { useRouter } from 'next/navigation'
import { ChangeEvent, FC, useCallback, useMemo, useState } from 'react'
import styles from './OnlineSearch.module.scss'

interface OnlineSearchProps {
  className?: string
}
export const OnlineSearch: FC<OnlineSearchProps> = observer(({ className }) => {
  const [searchValue, setSearchValue] = useState('')
  const router = useRouter()
  const [isMktuFilterVisible, setIsMktuFilterVisible] = useState(false)

  const onSearchFormSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      if (searchValue.length < 2) {
        return
      }
      router.push('/online-search?search=' + searchValue)
    },
    [router, searchValue]
  )

  const changeSearchValue = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setSearchValue(event.target.value)
    },
    []
  )

  const toggleMktuFilter = useCallback(() => {
    setIsMktuFilterVisible((isVisible) => !isVisible)
  }, [])

  const selectedMktuClasses = useMemo(
    () =>
      [...onlineSearchStore.selectedMktuClassesNumbers].sort(
        (firstClass, secondClass) => firstClass - secondClass
      ),
    [onlineSearchStore.selectedMktuClassesNumbers]
  )

  const deleteSelectedMktuClass = useCallback((classNumber: number) => {
    onlineSearchStore.deleteSelectedMktuClassNumber(classNumber)
  }, [])

  return (
    <div className={cn(styles.container, className)}>
      <div className={styles.title}>
        Поиск по базе зарегистрированных товарных знаков
      </div>
      <form
        action={'/online-search'}
        className={styles.form}
        onSubmit={onSearchFormSubmit}
      >
        <div className={styles.controls}>
          <FormInput
            name="search"
            className={styles.input}
            onChange={changeSearchValue}
            value={searchValue}
          />
          <Button
            className={styles.settingsBtn}
            type="button"
            onClick={toggleMktuFilter}
          >
            <Icon name="appSearchSettings" className={styles.settingsBtnIcon} />
          </Button>
          <Button type="submit" className={styles.submitButton}>
            Найти
          </Button>
        </div>
        {selectedMktuClasses.length !== 0 ? (
          <div className={styles.selectedClasses}>
            {selectedMktuClasses.map((classNumber) => (
              <MktuClass
                key={classNumber}
                classNumber={classNumber}
                onDelete={deleteSelectedMktuClass}
              />
            ))}
          </div>
        ) : null}
        {isMktuFilterVisible ? <AppSearchMktuFilterPanel /> : null}
      </form>
    </div>
  )
})

type MktuClassProps = {
  classNumber: number
  onDelete: (classNumber: number) => void
}
const MktuClass: FC<MktuClassProps> = ({ classNumber, onDelete }) => {
  const deleteMktuClass = useCallback(() => {
    onDelete(classNumber)
  }, [classNumber])

  return (
    <div className={styles.mktuClass}>
      Класс {classNumber}
      <button
        type="button"
        className={styles.mktuClassButton}
        onClick={deleteMktuClass}
      >
        <Icon name="greenClose" />
      </button>
    </div>
  )
}
